import { useState, useEffect } from "react";
import { useGetList } from "ra-core";
import {
  Shield,
  Users,
  Briefcase,
  DollarSign,
  FileText,
  TrendingUp,
  Phone,
  UserCheck,
} from "lucide-react";
import { KpiCard } from "./KpiCard";
import { OrgHealthTable } from "./OrgHealthTable";

interface Org {
  id: string;
  name: string;
  stripe_account_status: string;
}

interface Contact {
  id: string | number;
  org_id: string;
  contact_type: string;
}

interface Job {
  id: string;
  org_id: string;
  status: string;
  estimated_total: number;
}

interface Invoice {
  id: string;
  org_id: string;
  status: string;
  total: number;
}

interface Estimate {
  id: string;
  org_id: string;
  status: string;
  total: number;
}

function fmt(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Super Admin Dashboard — cross-org KPIs.
 * Only meaningful for super_admin users (RLS ensures cross-org SELECT).
 */
export function SuperAdminDashboard() {
  const { data: orgs, isPending: orgsLoading } = useGetList<Org>("organizations", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });

  const { data: contacts, isPending: contactsLoading } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "id", order: "DESC" },
  });

  const { data: jobs, isPending: jobsLoading } = useGetList<Job>("jobs", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "created_at", order: "DESC" },
  });

  const { data: invoices, isPending: invoicesLoading } = useGetList<Invoice>("invoices", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "created_at", order: "DESC" },
  });

  const { data: estimates, isPending: estimatesLoading } = useGetList<Estimate>("estimates", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "created_at", order: "DESC" },
  });

  const loading =
    orgsLoading || contactsLoading || jobsLoading || invoicesLoading || estimatesLoading;

  // Compute KPIs
  const totalOrgs = orgs?.length || 0;
  const totalContacts = contacts?.length || 0;
  const totalLeads = contacts?.filter((c) => c.contact_type === "lead").length || 0;
  const totalCustomers = contacts?.filter((c) => c.contact_type === "customer").length || 0;
  const conversionRate =
    totalLeads + totalCustomers > 0
      ? ((totalCustomers / (totalLeads + totalCustomers)) * 100).toFixed(1)
      : "0";

  const totalJobs = jobs?.length || 0;
  const activeJobs =
    jobs?.filter((j) => j.status === "in_progress" || j.status === "scheduled").length || 0;
  const completedJobs = jobs?.filter((j) => j.status === "completed" || j.status === "closed").length || 0;

  const totalRevenue =
    invoices
      ?.filter((i) => i.status === "paid")
      .reduce((sum, i) => sum + Number(i.total || 0), 0) || 0;

  const totalOutstanding =
    invoices
      ?.filter((i) => i.status === "sent" || i.status === "draft")
      .reduce((sum, i) => sum + Number(i.total || 0), 0) || 0;

  const totalEstimateValue =
    estimates?.reduce((sum, e) => sum + Number(e.total || 0), 0) || 0;

  const acceptedEstimates =
    estimates?.filter((e) => e.status === "accepted").length || 0;
  const totalEstimates = estimates?.length || 0;
  const estimateCloseRate =
    totalEstimates > 0
      ? ((acceptedEstimates / totalEstimates) * 100).toFixed(1)
      : "0";

  // Per-org health
  const orgHealthRows = (orgs || []).map((org) => {
    const orgContacts = contacts?.filter((c) => c.org_id === org.id).length || 0;
    const orgJobs = jobs?.filter((j) => j.org_id === org.id).length || 0;
    const orgRevenue =
      invoices
        ?.filter((i) => i.org_id === org.id && i.status === "paid")
        .reduce((sum, i) => sum + Number(i.total || 0), 0) || 0;
    const orgOutstanding =
      invoices
        ?.filter(
          (i) =>
            i.org_id === org.id &&
            (i.status === "sent" || i.status === "draft")
        )
        .reduce((sum, i) => sum + Number(i.total || 0), 0) || 0;

    return {
      id: org.id,
      name: org.name,
      contactCount: orgContacts,
      jobCount: orgJobs,
      revenue: orgRevenue,
      outstanding: orgOutstanding,
      stripeStatus: org.stripe_account_status,
    };
  });

  return (
    <div style={{ padding: 24 }}>
      <div className="flex items-center gap-2 mb-6">
        <Shield className="w-5 h-5 text-purple-600" />
        <h1 className="text-lg font-bold">Super Admin Dashboard</h1>
        <span className="text-xs text-muted-foreground bg-purple-50 text-purple-600 px-2 py-0.5 rounded-full font-medium">
          System-wide
        </span>
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground mb-4">Loading KPIs…</p>
      )}

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Organizations"
          value={totalOrgs.toString()}
          icon={Shield}
          color="#7c3aed"
        />
        <KpiCard
          label="Total Contacts"
          value={totalContacts.toLocaleString()}
          sublabel={`${totalLeads} leads · ${totalCustomers} customers`}
          icon={Users}
          color="#2563eb"
        />
        <KpiCard
          label="Conversion Rate"
          value={`${conversionRate}%`}
          sublabel="Lead → Customer"
          icon={UserCheck}
          color="#16a34a"
          trend={Number(conversionRate) > 25 ? "up" : Number(conversionRate) > 10 ? "flat" : "down"}
        />
        <KpiCard
          label="Active Jobs"
          value={activeJobs.toString()}
          sublabel={`${completedJobs} completed · ${totalJobs} total`}
          icon={Briefcase}
          color="#d97706"
        />
      </div>

      {/* Revenue row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard
          label="Revenue (Paid)"
          value={fmt(totalRevenue)}
          icon={DollarSign}
          color="#16a34a"
        />
        <KpiCard
          label="Outstanding"
          value={fmt(totalOutstanding)}
          icon={DollarSign}
          color="#d97706"
        />
        <KpiCard
          label="Estimate Pipeline"
          value={fmt(totalEstimateValue)}
          sublabel={`${totalEstimates} total estimates`}
          icon={FileText}
          color="#2563eb"
        />
        <KpiCard
          label="Estimate Close Rate"
          value={`${estimateCloseRate}%`}
          sublabel={`${acceptedEstimates} of ${totalEstimates} accepted`}
          icon={TrendingUp}
          color="#7c3aed"
          trend={Number(estimateCloseRate) > 40 ? "up" : Number(estimateCloseRate) > 20 ? "flat" : "down"}
        />
      </div>

      {/* Org health table */}
      <OrgHealthTable orgs={orgHealthRows} loading={orgsLoading} />
    </div>
  );
}

SuperAdminDashboard.path = "/super-admin";
